import type {
  Ability,
  ComfortBand,
  ComfortVerdict,
  DayOffset,
  SessionHour,
  SessionPreferences,
} from "./types";
import type { SessionContext } from "./comfort";
import { comfortVerdict, sportBandFor } from "./comfort";
import type { SupProfileConfidence } from "../conditions/sport-profiles";
import { SCORE_THRESHOLD } from "./windows";

export type HeatLens = "fit" | "level";

export type HeatCell = {
  hour: number;
  dayOffset: DayOffset;
  isDay: boolean;
  score: number;
  /** Null when there is no data for this hour (rendered as night). */
  verdict: ComfortVerdict | null;
  /** By-level lens: lowest ability the conditions suit; null when none. */
  suitsFrom: Ability | null;
  /** By-level lens: above the expert ceiling. */
  tooBigForAll: boolean;
};

export type Heatmap = {
  /** Daylight-band hour columns, union across both days (aligned). */
  hours: number[];
  rows: { dayOffset: DayOffset; label: "Today" | "Tomorrow"; cells: HeatCell[] }[];
};

export type SupProfileQualityHour = Omit<SessionHour, "score"> & {
  score: number | null;
  confidence: SupProfileConfidence;
};

export type SupProfileQualityHeatCell = {
  hour: number;
  dayOffset: DayOffset;
  isDay: boolean;
  score: number | null;
  confidence: SupProfileConfidence;
  state: "available" | "unavailable";
};

export type SupProfileQualityHeatmap = {
  hours: number[];
  rows: Array<{
    dayOffset: DayOffset;
    label: "Today" | "Tomorrow";
    cells: SupProfileQualityHeatCell[];
  }>;
};

const ABILITIES: Ability[] = ["beginner", "intermediate", "advanced", "expert"];

function suitability(
  prefs: SessionPreferences,
  h: SessionHour,
  ctx: SessionContext
): { suitsFrom: Ability | null; tooBigForAll: boolean } {
  let expertBand: ComfortBand | undefined;
  for (const ability of ABILITIES) {
    const band = sportBandFor(prefs, h, ability, ctx);
    if (ability === "expert") expertBand = band;
    if (band === "in-range" && h.score >= SCORE_THRESHOLD[ability]) {
      return { suitsFrom: ability, tooBigForAll: false };
    }
  }
  return { suitsFrom: null, tooBigForAll: expertBand === "too-strong" };
}

export function buildHeatmap(
  hours: readonly SessionHour[],
  prefs: SessionPreferences,
  ctx: SessionContext
): Heatmap {
  const dayHours = hours.filter((h) => h.isDay).map((h) => h.hour);
  const minH = dayHours.length ? Math.min(...dayHours) : 6;
  const maxH = dayHours.length ? Math.max(...dayHours) : 20;
  const cols: number[] = [];
  for (let hr = minH; hr <= maxH; hr++) cols.push(hr);

  const byKey = new Map<string, SessionHour>();
  for (const h of hours) byKey.set(`${h.dayOffset}:${h.hour}`, h);

  const dayOffsets: DayOffset[] = [0, 1];
  const rows = dayOffsets.map((dayOffset) => {
    const cells: HeatCell[] = cols.map((hour) => {
      const h = byKey.get(`${dayOffset}:${hour}`);
      if (!h) {
        return { hour, dayOffset, isDay: false, score: 0, verdict: null, suitsFrom: null, tooBigForAll: false };
      }
      const { suitsFrom, tooBigForAll } = suitability(prefs, h, ctx);
      return {
        hour,
        dayOffset,
        isDay: h.isDay,
        score: h.score,
        verdict: comfortVerdict(prefs, h, ctx),
        suitsFrom,
        tooBigForAll,
      };
    });
    return { dayOffset, label: dayOffset === 0 ? ("Today" as const) : ("Tomorrow" as const), cells };
  });

  return { hours: cols, rows };
}

/** A nullable named-profile score remains unavailable rather than becoming zero. */
export function buildHeatmapCell(
  hour: SupProfileQualityHour,
): SupProfileQualityHeatCell {
  const unavailable = hour.score === null || hour.confidence === "unavailable";
  return {
    hour: hour.hour,
    dayOffset: hour.dayOffset,
    isDay: hour.isDay,
    score: unavailable ? null : hour.score,
    confidence: hour.confidence,
    state: unavailable ? "unavailable" : "available",
  };
}

function unavailableSupProfileCell(
  hour: number,
  dayOffset: DayOffset,
): SupProfileQualityHeatCell {
  return {
    hour,
    dayOffset,
    isDay: false,
    score: null,
    confidence: "unavailable",
    state: "unavailable",
  };
}

/**
 * Source-quality view for named SUP profiles. Personal-fit inputs never alter
 * these scores or their confidence state.
 */
export function buildSupProfileQualityHeatmap(
  hours: readonly SupProfileQualityHour[],
): SupProfileQualityHeatmap {
  const daylightHours = hours.filter((hour) => hour.isDay).map((hour) => hour.hour);
  const minHour = daylightHours.length ? Math.min(...daylightHours) : 6;
  const maxHour = daylightHours.length ? Math.max(...daylightHours) : 20;
  const columns: number[] = [];
  for (let hour = minHour; hour <= maxHour; hour++) columns.push(hour);

  const byKey = new Map<string, SupProfileQualityHour>();
  for (const hour of hours) byKey.set(`${hour.dayOffset}:${hour.hour}`, hour);

  const rows = ([0, 1] as const).map((dayOffset) => ({
    dayOffset,
    label: dayOffset === 0 ? ("Today" as const) : ("Tomorrow" as const),
    cells: columns.map((hour) => {
      const source = byKey.get(`${dayOffset}:${hour}`);
      return source
        ? buildHeatmapCell(source)
        : unavailableSupProfileCell(hour, dayOffset);
    }),
  }));

  return { hours: columns, rows };
}
